/**
 * Airtable Metadata API Client
 * 
 * Client for interacting with Airtable's Metadata API
 * Used for retrieving table structure, field information, and schema details
 */

import { getCachedData, setCachedData } from './cache';
import { respectMetadataRateLimit } from './rate-limit';
import { ENDPOINTS } from './config';

// Types for Metadata API responses
export interface TableSchema {
  id: string;
  name: string;
  primaryFieldId: string;
  fields: FieldSchema[];
  views: ViewSchema[];
}

export interface FieldSchema {
  id: string;
  name: string;
  type: string;
  description?: string;
  options?: any;
}

export interface ViewSchema {
  id: string;
  name: string;
  type: string;
}

export interface BaseSchema {
  id: string;
  name: string;
  tables: TableSchema[];
}

/**
 * Generate a cache key for metadata requests
 */
function generateMetadataCacheKey(path: string, params?: Record<string, any>): string {
  return `metadata:${path}${params ? `:${JSON.stringify(params)}` : ''}`;
}

/**
 * Make a request to the Metadata API with caching
 */
async function metadataRequest<T>(
  path: string, 
  options: {
    method?: string;
    params?: Record<string, any>;
    body?: any;
    skipCache?: boolean;
    cacheTTL?: number;
  } = {}
): Promise<T> {
  await respectMetadataRateLimit();
  
  const {
    method = 'GET',
    params = {},
    body,
    skipCache = false,
    cacheTTL,
  } = options;
  
  // Check cache for GET requests if not explicitly skipped
  if (method === 'GET' && !skipCache) {
    const cacheKey = generateMetadataCacheKey(path, params);
    const cachedData = await getCachedData<T>(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }
  }
  
  // Build URL with query parameters
  let url = `${ENDPOINTS.METADATA_API}/${path}`;
  
  if (Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(item => queryParams.append(`${key}[]`, String(item)));
      } else {
        queryParams.append(key, String(value));
      }
    });
    url += `?${queryParams && typeof queryParams.toString === 'function' ? queryParams.toString() : String(queryParams)}`;
  }
  
  // Make the request
  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${process.env.AIRTABLE_PAT || process.env.NEXT_PUBLIC_AIRTABLE_PAT}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store', // For Next.js, don't cache at the fetch level
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Airtable Metadata API error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }
    
    const data = await response.json();
    
    // Store in cache for GET requests
    if (method === 'GET' && !skipCache) {
      const cacheKey = generateMetadataCacheKey(path, params);
      await setCachedData(cacheKey, data, cacheTTL);
    }
    
    return data as T;
  } catch (error) {
    console.error('Error accessing Airtable Metadata API:', error);
    throw error;
  }
}

/**
 * Get base schema metadata
 */
export async function getBaseSchema(options: { skipCache?: boolean; cacheTTL?: number } = {}): Promise<BaseSchema> {
  const { airtableConfig } = await import('./config');
  return metadataRequest<BaseSchema>(`bases/${airtableConfig.baseId}/tables`, {
    skipCache: options.skipCache,
    cacheTTL: options.cacheTTL,
  });
}

/**
 * Get metadata for a specific table
 */
export async function getTableSchema(
  tableIdOrName: string,
  options: { skipCache?: boolean; cacheTTL?: number } = {}
): Promise<TableSchema> {
  const { airtableConfig } = await import('./config');
  return metadataRequest<TableSchema>(`bases/${airtableConfig.baseId}/tables/${tableIdOrName}`, {
    skipCache: options.skipCache,
    cacheTTL: options.cacheTTL,
  });
}

/**
 * Get metadata for a field in a table
 */
export async function getFieldSchema(
  tableIdOrName: string,
  fieldIdOrName: string,
  options: { skipCache?: boolean; cacheTTL?: number } = {}
): Promise<FieldSchema> {
  return metadataRequest<FieldSchema>(
    `bases/${process.env.AIRTABLE_BASE_ID}/tables/${tableIdOrName}/fields/${fieldIdOrName}`,
    {
      skipCache: options.skipCache,
      cacheTTL: options.cacheTTL,
    }
  );
}

/**
 * Get all tables in the base
 */
export async function listTables(
  options: { skipCache?: boolean; cacheTTL?: number } = {}
): Promise<TableSchema[]> {
  const baseSchema = await getBaseSchema(options);
  console.log('[Airtable][Schema] Full base schema:', JSON.stringify(baseSchema, null, 2));
  return baseSchema.tables;
}

/**
 * Get all field definitions for a table
 */
export async function listFields(
  tableIdOrName: string,
  options: { skipCache?: boolean; cacheTTL?: number } = {}
): Promise<FieldSchema[]> {
  const tableSchema = await getTableSchema(tableIdOrName, options);
  return tableSchema.fields;
}

/**
 * Get dynamic schema for a specific table by name or ID (with caching)
 */
export async function getDynamicTableSchema(tableNameOrId: string, options: { skipCache?: boolean; cacheTTL?: number } = {}) {
  const tables = await listTables(options);
  const table = tables.find(t => t.name === tableNameOrId || t.id === tableNameOrId);
  if (!table) {
    console.error(`[getDynamicTableSchema] Table not found. Searched for:`, tableNameOrId);
    console.error(`[getDynamicTableSchema] Available tables:`, tables.map(t => ({ id: t.id, name: t.name })));
    throw new Error(`Table not found: ${tableNameOrId}`);
  }
  return table;
}

/**
 * Get dynamic base schema (all tables and fields, with caching)
 */
export async function getDynamicBaseSchema(options: { skipCache?: boolean; cacheTTL?: number } = {}) {
  return await listTables(options);
}

// --- SCHEMA INTROSPECTION SCRIPT ---
// Run with: npx ts-node src/lib/airtable/metadata-api.ts
if (require.main === module) {
  (async () => {
    try {
      const tables = await listTables({ skipCache: true });
      console.log('Airtable Tables and Fields:');
      for (const table of tables) {
        console.log(`- ${table.name}`);
        for (const field of table.fields) {
          console.log(`    - ${field.name} (${field.type})`);
        }
      }
    } catch (err) {
      console.error('Error fetching Airtable schema:', err);
      process.exit(1);
    }
  })();
} 
